import Icon from 'antd/lib/icon';
import Tabs from 'antd/lib/tabs';
import * as React from 'react';
import { useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import {
  getContainerGroupKey,
  getContainerGroupLabel,
  quotesSpanMultipleVendors,
} from '../../../SE.constants';
import { QuoteType, SLQuoteGroupType } from '../../../SE.interfaces';
import SEQuotes from './SEQuotes';
import { SEShippingLineSizeTabsStyles } from './SEShippingLineSizeTabs.styles';

const TabPane = Tabs.TabPane;

const styles = () => ({ ...SEShippingLineSizeTabsStyles() });

/**
 * The slice of the RFQ_QUOTE field model this component touches. The field carries the saved value
 * (a single quote historically, now the array of per-size picks for SL) and the setter used to persist
 * the current picks back onto the form field.
 */
export interface SEQuoteFieldModel {
  value?: QuoteType | QuoteType[];
  setValue: (value: QuoteType[]) => void;
  disabled?: boolean;
}

interface SEShippingLineSizeTabsProps extends StylesProps<ReturnType<typeof styles>> {
  /** One entry per container size, each independently ranked by the backend. */
  groups: SLQuoteGroupType[];
  /** The RFQ_QUOTE field model (SMF430). Its value is written as the ARRAY of per-size picks. */
  field?: SEQuoteFieldModel;
  /** Vendor-type key so the task footer can aggregate this tab's array with the other single picks. */
  fieldKey: string;
  reportSelection: (fieldKey: string, quotes: QuoteType | QuoteType[] | undefined) => void;
  disabled?: boolean;
  /**
   * ISS-293278 — increments when the parent switches the rate source (Spot⇄Tender). On a change we drop the
   * per-size picks so a selection from the previous rate book is not carried into the refreshed candidates.
   */
  rateBookVersion?: number;
  /**
   * ISS-292834 — display-only GPO metadata. `vendorSelectionFields` (the vendor's own Select-Vendor-Types
   * sub-fields) is passed down to each card; `shipmentMatchFactors` is shipment-level and constant across
   * every size, so it is rendered ONCE above the tab strip rather than inside each size tab.
   */
  vendorSelectionFields?: { label: string; value: string }[];
  shipmentMatchFactors?: { label: string; value: string }[];
}

/**
 * Shipping Line (SMF430) container-size split (ISS-291047, Phase 2). Renders one inner tab per container
 * size — each size's quotes ranked independently — and shows the FULL ranked list in every size tab at
 * all times: selecting a vendor in one size never hides or filters any list (not its own, not the others),
 * so the user can freely pick and change vendors per size.
 *
 * The "same Shipping Line across all sizes" rule is a VALIDATION, not a filter: a mismatched selection can
 * be assembled and is flagged with a warning banner here, but submit is blocked at the task level (see the
 * gate in SEAddTaskDetailModal.handleSubmit; the backend hard-blocks as a backstop).
 *
 * Reports UP the array of per-size picks (in group order) and writes the same array onto the field so the
 * submit payload serialises to `values: [pick20, pick40, ...]`; the combined amount + L1 deviation flow
 * into the existing task-level footer/gate via the flattened selection.
 */
const SEShippingLineSizeTabs = (props: SEShippingLineSizeTabsProps): React.ReactElement => {
  const {
    classes, groups, field, fieldKey, reportSelection, disabled, rateBookVersion, vendorSelectionFields, shipmentMatchFactors,
  } = props;

  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, QuoteType | undefined>>({});
  const [isInitialAssignment, setIsInitialAssignment] = useState<boolean>(true);

  // Hydrate previously-saved picks (SMF430 stores the array of chosen quotes). Each saved quote is
  // slotted back under its own container-size group key.
  useEffect(() => {
    if (!isInitialAssignment) return;
    const savedPicks: QuoteType[] = Array.isArray(field?.value)
      ? field.value
      : field?.value
        ? [field.value]
        : [];
    if (!savedPicks.length) return;
    const hydrated: Record<string, QuoteType | undefined> = {};
    savedPicks.forEach((pick) => {
      hydrated[getContainerGroupKey({
        containerSize: pick?.containerSize ?? null,
        containerType: pick?.containerType ?? null,
      })] = pick;
    });
    setSelectedByGroup(hydrated);
    setIsInitialAssignment(false);
  }, [field?.value, isInitialAssignment]);

  // ISS-293278 — the parent switched the rate source: drop every per-size pick from the previous book. The
  // persist effect below then writes the empty array to the field and clears this tab's footer contribution.
  // Skip the initial render (version 0) so first-load hydration above is preserved.
  useEffect(() => {
    if (!rateBookVersion) return;
    setSelectedByGroup({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateBookVersion]);

  // Ordered, defined picks — this is exactly what we report up and persist onto the field.
  const orderedPicks = groups
    .map((group) => selectedByGroup[getContainerGroupKey(group)])
    .filter((pick): pick is QuoteType => Boolean(pick));

  // Same-vendor rule is validated, not filtered: flag (and, at the task level, block) when the current
  // picks span more than one distinct Shipping Line vendor.
  const hasVendorMismatch = quotesSpanMultipleVendors(orderedPicks);

  useEffect(() => {
    // Never mutate the field on the read-only (approval) view, but still report the hydrated picks so
    // the task-level footer total includes Shipping Line — matching how every other panel reports even
    // when disabled.
    if (!disabled && field) field.setValue(orderedPicks);
    reportSelection(fieldKey, orderedPicks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedByGroup]);

  const onGroupSelection = (groupKey: string, quote: QuoteType) => {
    setSelectedByGroup((prev) => ({ ...prev, [groupKey]: quote }));
  };

  const resetGroup = (groupKey: string) => {
    setSelectedByGroup((prev) => ({ ...prev, [groupKey]: undefined }));
  };

  return (
    <div className={classes.container}>
      {!disabled && hasVendorMismatch && (
        <div className={classes.vendorMismatchNote}>
          All container sizes must be from the same Shipping Line vendor. Selections differ — align them
          before submitting.
        </div>
      )}
      <Tabs type="card">
        {groups.map((group) => {
          const groupKey = getContainerGroupKey(group);
          const selectedQuote = selectedByGroup[groupKey];
          // Show the FULL ranked list for every size — selecting a vendor never filters any list.
          const quoteList = group.quotes;
          return (
            <TabPane
              key={groupKey}
              tab={(
                <span className={classes.tabLabel}>
                  {selectedQuote && (
                    <Icon type="check-circle" theme="filled" className={classes.tabSelectedIcon} />
                  )}
                  {getContainerGroupLabel(group)}
                </span>
              )}
              forceRender
            >
              <SEQuotes
                selectedQuote={selectedQuote}
                quoteList={quoteList}
                onQuoteSelection={(quote) => onGroupSelection(groupKey, quote)}
                resetQuote={() => resetGroup(groupKey)}
                disabled={disabled}
                vendorSelectionFields={vendorSelectionFields}
                shipmentMatchFactors={shipmentMatchFactors}
              />
            </TabPane>
          );
        })}
      </Tabs>
    </div>
  );
};

export default withStyles(styles)(SEShippingLineSizeTabs);
